import React, { useState, useEffect, useCallback } from "react";
import { Select, DatePicker } from "antd";
import moment from "moment";
import styled from "styled-components";
import { safemomentmin, safemomentmax } from "@/containers/UtilityData/New-Summary/utils";

const FormLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  margin-right: 6px;
`;

const FilterBar = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  padding: 12px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledSelect = styled(Select)`
  &.ant-select {
    min-width: 120px;

    .ant-select-selector {
      border-radius: 6px;
      height: 32px;
      padding: 0 8px;
      border-color: #e5e7eb;

      .ant-select-selection-item {
        line-height: 30px;
        font-weight: 500;
        color: #374151;
      }
    }

    &:hover .ant-select-selector {
      border-color: #d1d5db;
    }
  }
`;

const StyledDatePicker = styled(DatePicker)`
  &.ant-picker {
    border-radius: 6px;
    height: 32px;
    border-color: #e5e7eb;

    &:hover {
      border-color: #d1d5db;
    }

    .ant-picker-input > input {
      font-size: 13px;
      color: #374151;
    }
  }
`;

/**
 * SelectMonthRange组件
 * @param {Object} props
 * @param {number} props.month - 月份数量
 * @param {string} props.ending_date - 结束日期 (YYYY-MM格式)
 * @param {Function} props.onChange - 变化回调函数，参数为 {month, ending_date}
 * @param {Object} props.dateRange - 日期范围限制，可选
 */
const SelectMonthRange = ({ 
  month = 12, 
  ending_date, 
  onChange, 
  dateRange = null 
}) => {
  // 计算默认的结束月份（当前年的前一个月）
  const getDefaultEndingMonth = () => {
    return moment().subtract(1, 'month').format('YYYY-MM');
  };

  // 初始化状态
  const [period, setPeriod] = useState("12"); // 默认1 Year
  const [endingMonth, setEndingMonth] = useState(ending_date || getDefaultEndingMonth());
  const [rangeMonth, setRangeMonth] = useState(() => {
    if (dateRange) {
      const max = safemomentmax(
        moment(dateRange.date_range_elec_max),
        moment(dateRange.date_range_gas_max)
      );
      const min = safemomentmin(
        moment(dateRange.date_range_elec_min),
        moment(dateRange.date_range_gas_min)
      );
      // 确保起始日期不会晚于结束日期
      const startDate = moment.max(min, max.clone().subtract(11, "month"));
      return [startDate, max];
    }
    // 如果没有dateRange，使用默认值
    const endDate = moment(ending_date || getDefaultEndingMonth());
    const startDate = endDate.clone().subtract(11, "month");
    return [startDate, endDate];
  });

  // 计算结果值
  let resultPeriod = period;
  let resultEndingMonth = moment(endingMonth).format("YYYY-MM");
  let resultDateRange = rangeMonth;

  if (period === "custom") {
    resultPeriod = rangeMonth[1].diff(rangeMonth[0], "month") + 1;
    resultEndingMonth = rangeMonth[1].format("YYYY-MM");
  } else if (period === "YTD") {
    resultPeriod = moment().diff(moment().startOf("year"), "month") + 1;
    resultEndingMonth = moment().format("YYYY-MM");
  } else if (period === "All") {
    if (dateRange) {
      const range = [
        safemomentmin(
          moment(dateRange.date_range_elec_min),
          moment(dateRange.date_range_gas_min)
        ),
        safemomentmax(
          moment(dateRange.date_range_elec_max),
          moment(dateRange.date_range_gas_max)
        ),
      ];
      resultPeriod = range[1].diff(range[0], "month") + 1;
      resultEndingMonth = range[1].format("YYYY-MM");
      resultDateRange = range;
    }
  }

  // 当结果值变化时，调用onChange回调
  useEffect(() => {
    if (onChange) {
      console.log('SelectMonthRange onChange 触发:', { month: resultPeriod, ending_date: resultEndingMonth });
      onChange({
        month: resultPeriod,
        ending_date: resultEndingMonth
      });
    }
  }, [resultPeriod, resultEndingMonth]);

  // 处理日期禁用逻辑
  const getDisabledDate = (current) => {
    if (!dateRange) return false;
    
    const max = safemomentmax(
      moment(dateRange.date_range_elec_max),
      moment(dateRange.date_range_gas_max)
    );
    const min = safemomentmin(
      moment(dateRange.date_range_elec_min),
      moment(dateRange.date_range_gas_min)
    );
    return (
      current.isAfter(max, "month") ||
      current.isBefore(min, "month")
    );
  };

  return (
    <FilterBar>
      <StyledSelect
        allowClear={false}
        value={period}
        onChange={setPeriod}
        placeholder="Select Period"
      >
        <Select.Option value="custom">Custom</Select.Option>
        <Select.Option value="1">1 Month</Select.Option>
        <Select.Option value="3">3 Month</Select.Option>
        <Select.Option value="6">6 Month</Select.Option>
        <Select.Option value="YTD">YTD</Select.Option>
        <Select.Option value="12">1 Year</Select.Option>
        <Select.Option value="24">2 Year</Select.Option>
        <Select.Option value="36">3 Year</Select.Option>
        <Select.Option value="60">5 Year</Select.Option>
        <Select.Option value="All">All</Select.Option>
      </StyledSelect>

      {period === "custom" || period === "All" ? (
        <>
          <FormLabel>Range Month:</FormLabel>
          <StyledDatePicker.RangePicker
            disabledDate={getDisabledDate}
            disabled={period === "All"}
            value={resultDateRange}
            onChange={(dates) => {
              if (dates && dates.length === 2) {
                setRangeMonth(dates);
              }
            }}
            picker="month"
            style={{ minWidth: 260 }}
          />
        </>
      ) : (
        period !== "YTD" && (
          <>
            <FormLabel>Ending Month:</FormLabel>
            <StyledDatePicker
              allowClear={false}
              value={moment(resultEndingMonth)}
              onChange={(value) => {
                setEndingMonth(value.format("YYYY-MM"));
              }}
              picker="month"
              disabledDate={getDisabledDate}
            />
          </>
        )
      )}
    </FilterBar>
  );
};

export default SelectMonthRange;

/**
 * 使用示例：
 * 
 * import SelectMonthRange from '@/containers/PortfolioManage/Dashboard/components/SelectMonthRange';
 * 
 * const MyComponent = () => {
 *   const [dateRange, setDateRange] = useState({
 *     month: 12,
 *     ending_date: '2025-07'
 *   });
 * 
 *   const handleDateRangeChange = (newRange) => {
 *     setDateRange(newRange);
 *     console.log('月份数量:', newRange.month);
 *     console.log('结束日期:', newRange.ending_date);
 *   };
 * 
 *   return (
 *     <div>
 *       <SelectMonthRange
 *         month={dateRange.month}
 *         ending_date={dateRange.ending_date}
 *         onChange={handleDateRangeChange}
 *         dateRange={dateRangeData} // 可选，用于限制日期范围
 *       />
 *     </div>
 *   );
 * };
 */
